import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsConge, useSoldesRh, useStructures } from '@/api/hooks';
import { LIBELLE_REGIME } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr } from '@/lib/utils';

/** Droits et soldes (gestion RH, § 4.7) : période en cours de chaque agent, régularisations tracées, reprise des soldes. */
export function DroitsRh() {
  const [q, setQ] = useState('');
  const [structure, setStructure] = useState('');
  const structures = useStructures();
  const { data, isLoading, error } = useSoldesRh({ q: q || undefined, structure: structure || undefined });
  const actions = useActionsConge();
  const [regul, setRegul] = useState<{ agentId: string; nom: string; periodeId?: string } | null>(null);
  const [jours, setJours] = useState('');
  const [motif, setMotif] = useState('');
  const [reprise, setReprise] = useState(false);
  const [lignesTexte, setLignesTexte] = useState('');
  const [rapport, setRapport] = useState<{ erreurs: Array<{ ligne: number; message: string }>; lignes: number; ecrites: number } | null>(null);
  const regulariser = async () => {
    if (!regul) return;
    try {
      await actions.regulariser.mutateAsync({ agentId: regul.agentId, periodeId: regul.periodeId, jours: Number(jours), motif });
      toast.success('Solde régularisé (journalisé).');
      setRegul(null);
      setJours('');
      setMotif('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Régularisation impossible.');
    }
  };
  const lignesReprise = () =>
    lignesTexte
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [matricule, dateReference, report, pris] = l.split(/[;\t]/).map((x) => x?.trim() ?? '');
        return { matricule: matricule ?? '', dateReference: dateReference ?? '', joursReportRecu: report ? Number(report) : undefined, joursPris: pris ? Number(pris) : undefined };
      });
  const lancerReprise = async (simulation: boolean) => {
    try {
      const r = await actions.reprise.mutateAsync({ lignes: lignesReprise(), simulation });
      setRapport(r);
      if (!r.erreurs.length && !simulation) toast.success(`${r.ecrites} solde(s) repris.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Reprise impossible.');
    }
  };
  return (
    <div>
      <PageHeader
        titre="Droits et soldes de congé"
        sousTitre="Période en cours de chaque agent (anniversaire de l'engagement), droits, report, pris, approuvés, en attente, disponible ; régularisations motivées et journalisées."
        actions={
          <Button variant="outline" onClick={() => setReprise(true)}>
            Reprise des soldes
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input className="w-64" placeholder="Nom, matricule…" value={q} onChange={(e) => setQ(e.target.value)} />
        <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)}>
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucun agent." />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Régime</TableHead>
                <TableHead>Période en cours</TableHead>
                <TableHead className="text-right">Droits</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Pris · approuvés · en attente</TableHead>
                <TableHead className="text-right">Disponible</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((s) => (
                <TableRow key={s.agent.id}>
                  <TableCell>
                    <Link to={`/rh/agents/${s.agent.id}`} className="font-medium text-primary hover:underline">
                      {s.agent.prenom} {s.agent.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {s.agent.matricule} · {s.agent.structureCode}
                      {s.courante && (
                        <>
                          {' · '}
                          <button type="button" className="text-primary hover:underline" onClick={() => setRegul({ agentId: s.agent.id, nom: `${s.agent.prenom} ${s.agent.nom}`, periodeId: s.courante?.id })}>
                            Régulariser
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{s.regimeConge ? LIBELLE_REGIME[s.regimeConge].split(' (')[0] : <Badge variant="outline">à confirmer</Badge>}</TableCell>
                  <TableCell>
                    {s.courante ? (
                      <>
                        du {dateFr(s.courante.debut)} au {dateFr(s.courante.fin)}
                        {s.courante.alerteExpiration && <Badge variant="destructive" className="ml-1">expire bientôt</Badge>}
                      </>
                    ) : s.dateEngagement ? (
                      <span className="text-muted-foreground">{s.douzeMoisDeService ? 'régime à confirmer' : 'droit non ouvert (moins de douze mois)'}</span>
                    ) : (
                      <span className="text-amber-700">date d'engagement à renseigner</span>
                    )}
                  </TableCell>
                  <TableCell className="num text-right">{s.courante ? `${s.courante.droits}` : '—'}</TableCell>
                  <TableCell className="num text-right hidden sm:table-cell">{s.courante ? `${s.courante.pris} · ${s.courante.approuves} · ${s.courante.enAttente}` : '—'}</TableCell>
                  <TableCell className="num text-right font-medium">{s.courante?.disponible ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={Boolean(regul)} onOpenChange={(o) => !o && setRegul(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Régulariser le solde de {regul?.nom}</DialogTitle>
            <DialogDescription>Jours ouvrables ajoutés (positif) ou retirés (négatif) sur la période en cours ; motif obligatoire, ancienne et nouvelle valeur journalisées.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="rjours">Jours</Label>
              <Input id="rjours" type="number" min={-60} max={60} value={jours} onChange={(e) => setJours(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rmotif">Motif (10 caractères au moins)</Label>
              <Textarea id="rmotif" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegul(null)}>
              Annuler
            </Button>
            <Button onClick={regulariser} disabled={!jours || Number(jours) === 0 || motif.trim().length < 10 || actions.regulariser.isPending}>
              Régulariser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reprise} onOpenChange={setReprise}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reprise des soldes de congé</DialogTitle>
            <DialogDescription>Une ligne par agent : matricule ; date de référence (AAAA-MM-JJ) ; report reçu de la période précédente ; jours déjà pris sur la période en cours. Contrôle tout ou rien ; simulez avant d'écrire.</DialogDescription>
          </DialogHeader>
          <Textarea rows={8} placeholder={'F-0007;2027-01-01;5;4\nF-0021;2027-01-01;0;0'} value={lignesTexte} onChange={(e) => setLignesTexte(e.target.value)} />
          {rapport && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {rapport.erreurs.length ? `${rapport.erreurs.length} erreur(s)` : rapport.ecrites ? `${rapport.ecrites} solde(s) écrit(s)` : `${rapport.lignes} ligne(s) valide(s), rien d'écrit`}
                </CardTitle>
                {rapport.erreurs.length > 0 && <CardDescription>Corrigez puis relancez : aucune ligne n'a été écrite.</CardDescription>}
              </CardHeader>
              {rapport.erreurs.length > 0 && (
                <CardContent className="text-sm">
                  {rapport.erreurs.map((e, i) => (
                    <div key={i}>
                      Ligne {e.ligne} : {e.message}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => lancerReprise(true)} disabled={!lignesTexte.trim() || actions.reprise.isPending}>
              Simuler
            </Button>
            <Button onClick={() => lancerReprise(false)} disabled={!lignesTexte.trim() || actions.reprise.isPending}>
              Écrire les soldes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
